import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../services/supabase';
import { useBusiness } from '../../../contexts/BusinessContext';
import SubscriptionConversion from './SubscriptionConversion';
import SubscriptionSubscribed from './SubscriptionSubscribed';
import { ArrowLeft, Beaker } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SubscriptionManagement = () => {
  const { user } = useAuth();
  const { business } = useBusiness();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDevControls, setShowDevControls] = useState(false);
  const [simulatedStatus, setSimulatedStatus] = useState('trial');
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.id || !business?.id) return;

    const fetchSubscriptionData = async () => {
      try {
        setLoading(true);
        
        // Por ahora simularemos los datos de suscripción
        // Esto se reemplazará con una consulta real a Supabase cuando las tablas existan
        const mockSubscriptionData = {
          status: simulatedStatus, // 'active', 'trial', 'canceled', 'free'
          current_period_end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 días en el futuro
          plan_name: 'Plus'
        };
        
        // Esta será la consulta real cuando la tabla exista
        /*
        const { data, error } = await supabase
          .from('subscriptions')
          .select('status, current_period_end, subscription_plans(name)')
          .eq('user_id', user.id)
          .eq('business_id', business.id)
          .single();

        if (error) throw error;
        
        setSubscriptionStatus(data || { status: 'free' });
        */
        
        // Usar datos de prueba por ahora
        setSubscriptionStatus(mockSubscriptionData);
      } catch (error) {
        console.error('Error al obtener datos de suscripción:', error);
        setSubscriptionStatus({ status: 'free' });
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, [user?.id, business?.id, simulatedStatus]);

  const toggleDevControls = () => {
    setShowDevControls(!showDevControls);
  };

  const handleStatusChange = (status) => {
    setSimulatedStatus(status);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Cabecera */}
      <div className="bg-white dark:bg-gray-800 shadow-sm p-4 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            aria-label="Volver"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Suscripción</h1>
        </div>
        
        {/* Botón para mostrar controles de desarrollo */}
        <button
          onClick={toggleDevControls}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          aria-label="Controles de desarrollo"
        >
          <Beaker className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
      </div>
      
      {/* Controles de desarrollo */}
      {showDevControls && (
        <div className="bg-gray-100 dark:bg-gray-800 p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="container mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 items-center">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Simular estado:</div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleStatusChange('free')}
                  className={`px-3 py-1 rounded-md text-sm ${
                    simulatedStatus === 'free' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Gratuito
                </button>
                <button
                  onClick={() => handleStatusChange('trial')}
                  className={`px-3 py-1 rounded-md text-sm ${
                    simulatedStatus === 'trial' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Prueba
                </button>
                <button
                  onClick={() => handleStatusChange('active')}
                  className={`px-3 py-1 rounded-md text-sm ${
                    simulatedStatus === 'active' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Suscriptor
                </button>
                <button
                  onClick={() => handleStatusChange('canceled')}
                  className={`px-3 py-1 rounded-md text-sm ${
                    simulatedStatus === 'canceled' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  Cancelado
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Modo de desarrollo: estos controles son solo para simular diferentes estados de suscripción.
            </p>
          </div>
        </div>
      )}

      {/* Contenido condicional según estado de suscripción */}
      <div className="container mx-auto px-4 py-6">
        {subscriptionStatus?.status === 'active' ? (
          <SubscriptionSubscribed subscription={subscriptionStatus} />
        ) : (
          <SubscriptionConversion subscription={subscriptionStatus} />
        )}
      </div>
    </div>
  );
};

export default SubscriptionManagement; 